/**
 * -----------------------------------------------------------------------------
 * Popup - Create Custom Icon
 * -----------------------------------------------------------------------------
 * This popup is used for uploading a new custom icon.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";
import svgIcons from "../../../assets/svg/svgIcons";
import { useUploadCustomAssetImageMutation } from "../../../api/network/adminApiServices";

const CreateCustomIconModal = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.import_export",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const [uploadCustomAssetIcon] = useUploadCustomAssetImageMutation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateCustomIcon: false }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG and JPG formats are supported");
      return;
    }

    // Validate file size (e.g., max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    // Dimension validation
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = img;
      if (width < 32 || height < 32) {
        toast.error("Image must be at least 32×32 px");
        return;
      }
      if (width > 256 || height > 256) {
        toast.error("Image must not exceed 256×256 px");
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    };
    img.src = objectUrl;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an icon to upload");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);

      await uploadCustomAssetIcon(formData).unwrap();

      toast.success("Custom icon uploaded successfully!");
      hideModal();
    } catch (error: any) {
      const message =
        error?.data?.message || error?.data?.detail || "Failed to upload icon";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <FTModal
      visible={!!modalsState.showCreateCustomIcon}
      heading="Add Custom Icon"
      onHide={hideModal}
      disableOverlay={true}
    >
      <div className="grid grid-cols-1 gap-6">
        {/* Drag and Drop Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            id="icon-upload"
          />

          {previewUrl ? (
            // Preview
            <div className="flex flex-col items-center gap-4">
              <div className="size-24 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <p className="text-sm text-gray-600 break-all">
                {selectedFile?.name}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Remove
              </button>
            </div>
          ) : (
            // Upload prompt
            <div className="flex flex-col items-center gap-4">
              <img
                src={svgIcons.Upload}
                alt="Upload"
                className="size-12 opacity-40"
              />
              <div>
                <p className="text-gray-600 mb-2">Drag and drop, or</p>
                <label
                  htmlFor="icon-upload"
                  className="text-blue-600 hover:text-blue-800 cursor-pointer font-semibold underline"
                >
                  Browse files
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Supported formats: PNG, JPG (Max 5MB)
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <FormActionButtons
          buttons={[
            {
              label: isUploading ? (
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="Loading"
                  className="size-5"
                />
              ) : (
                "Upload"
              ),
              variant: "primary",
              disabled: !selectedFile || isUploading,
              onClick: handleUpload,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isUploading,
              onClick: hideModal,
            },
          ]}
        />
      </div>
    </FTModal>
  );
};

export default CreateCustomIconModal;
